import React, {useState} from 'react';
import {List, Set} from 'immutable';
import classnames from 'classnames';
import isEmail from 'validator/lib/isEmail';
import {useTracking} from 'react-tracking';
import errorCodes from 'client/errorCodes';
import {useFluxStore} from 'client/framework';
import classroomV2Store from 'client/Classrooms/Classroom/ClassroomV2.store';

import withTracking from 'lib/hocs/withTracking';
import {doesUserBelongToAnyLicensedClassrooms} from 'lib/UserAccessUtil';
import ArrayToReadableFragment from 'lib/hocs/arrayToReadableFragment';
import {arrayToReadableString} from 'lib/stringUtils';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';

import {Banner, Dialogue, LoadingSpinnerLong, TextAreaField} from '@albert-io/atomic';
import TrackedButton from 'client/components/Tracking/TrackedButton';
import HardCapWarningBanner from 'client/components/HardCapWarningBanner/HardCapWarningBanner.react';

import {isManagedAccount} from 'client/Classrooms/shared/enrollUtils';

import IBESummary from './IBEDialogues/IBESummary.react';
import IBEConfirmEmails from './IBEDialogues/IBEConfirmEmails.react';
import UploadCSVBtn from './UploadCSVBtn/UploadCSVBtn.react';
import EmailChips from './EmailChips/EmailChips.react';
import {bulkCreateClassroomInvitations} from './lib/bulkCreateClassroomInvitations';
import './invite-by-email.scss';

const delimiter = new RegExp(/[\n\r;, ]/, 'gi');

const TrackedTextAreaField = withTracking(TextAreaField, {
  onPaste: {
    content: (e: React.ClipboardEvent) => e.clipboardData.getData('Text'),
    event: 'paste',
    type: null
  }
});

interface Props {
  classroomId?: string;
  emailDomains?: List<any>;
  handleClose: () => void;
  inModal?: boolean;
  onSend?: () => void;
  restrictClassroomEnrollment?: boolean;
  students?: List<any>;
  hardCap?: boolean;
  seatsRemaining?: number;
  className?: string;
  size?: 'full' | 'xl' | 'l' | 'm' | 's';
  hideCloseBtn?: false;
}

export default function InviteByEmail({
  classroomId,
  emailDomains = List(),
  handleClose,
  onSend = () => {},
  restrictClassroomEnrollment,
  students = List(),
  hardCap,
  seatsRemaining,
  ...rest
}: Props) {
  const {Track, trackEvent} = useTracking({component: 'InviteByEmail'});
  const invitations = useFluxStore(
    classroomV2Store,
    (s) => s.getClassroomInvitations(classroomId),
    (a, b) => a.equals(b)
  );
  const [emails, setEmails] = useState<Set<string>>(Set());
  const [error, setError] = useState<string | null>(null);
  const [fulfilledInvitations, setFulfilledInvitations] = useState<any>([]);
  const [mustConfirmSubmission, setMustConfirmSubmission] = useState(false);
  const [rejectedInvitations, setRejectedInvitations] = useState<any>([]);
  const [hardCapRejectedInvitations, setHardCapRejectedInvitations] = useState<any>([]);
  const [requestInFlight, setRequestInFlight] = useState(false);
  const [value, setValue] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setError(null);
    setValue(e.target.value);
  };

  const doesInvitationAlreadyExist = (email?: string) =>
    students.some((student) => student.getEmail() === email) ||
    invitations.some((invitation) => invitation.getEmail() === email);

  const handleSubmit = (inputValue: string = value) => {
    const input = inputValue.trim().split(delimiter);
    const parsedInput = input
      .filter((item) => item) // filter out empty strings
      .reduce<{invalidItems: Array<{email: string; reason: string}>; validItems: string[]}>(
        (acc, curr) => {
          if (!isEmail(curr)) {
            acc.invalidItems.push({
              email: curr,
              reason: 'One or more of your entries is not a valid email.'
            });
            return acc;
          }
          if (
            restrictClassroomEnrollment &&
            !emailDomains.isEmpty() &&
            !isManagedAccount(curr, emailDomains)
          ) {
            const domainNames = emailDomains.map((domain) => domain.getName()).toArray();
            acc.invalidItems.push({
              email: curr,
              reason: `Invitations are restricted to emails ending in ${arrayToReadableString(
                domainNames,
                'or'
              )}.`
            });
            return acc;
          }
          acc.validItems.push(curr);
          return acc;
        },
        {
          invalidItems: [],
          validItems: []
        }
      );

    if (parsedInput.invalidItems.length) {
      trackEvent({
        content: parsedInput.invalidItems[0].reason,
        event: 'error',
        type: null
      });
    }

    setEmails((prev) => prev.union(parsedInput.validItems));
    setError(parsedInput.invalidItems.length ? parsedInput.invalidItems[0].reason : null);
    setValue(parsedInput.invalidItems.map((item) => item.email).join(';\n'));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      trackEvent({content: null, event: 'keyboard', type: 'enter'});
      handleSubmit();
    }
  };

  const handleCSVUpload = (uploadedEmails: string[]) => {
    trackEvent({content: null, event: 'csv_upload', type: null});
    const updatedValue = value
      .trim()
      .split(delimiter)
      .filter((item) => item) // Remove any empty strings among items
      .concat(uploadedEmails)
      .join(';\n');
    setValue(updatedValue);
    handleSubmit(updatedValue);
  };

  const handleSend = async () => {
    setRequestInFlight(true);

    const invitationsPayload = emails
      .filterNot(doesInvitationAlreadyExist)
      .toArray()
      .map((email) => ({
        classroom_id: classroomId,
        email,
        issuer_id: masqueradeStore.getUserIdByMasqueradeState()
      }));

    const parsedResponse: {fulfilled: any; rejected: any; hardCapRejected: any} = {
      fulfilled: [],
      rejected: [],
      hardCapRejected: []
    };

    try {
      const response = await bulkCreateClassroomInvitations(invitationsPayload);
      parsedResponse.fulfilled = response!
        .toJS()
        .map((invitation: {email: string}) => invitation.email)
        .sort();
    } catch (reason: any) {
      if (
        reason?.response?.body?.errors?.find(
          (err: {code: string}) => err.code === errorCodes.FORBIDDEN.EXCEEDED_HARD_CAP_LIMIT
        )
      ) {
        parsedResponse.hardCapRejected = emails;
      } else {
        parsedResponse.rejected = emails;
      }
    }

    setEmails((prev) => prev.subtract(parsedResponse.fulfilled));
    setFulfilledInvitations(parsedResponse.fulfilled);
    setMustConfirmSubmission(false);
    setRejectedInvitations(parsedResponse.rejected);
    setHardCapRejectedInvitations(parsedResponse.hardCapRejected);
    setRequestInFlight(false);

    if (parsedResponse.fulfilled.length > 0) {
      onSend();
    }
  };

  const removeEmail = (email: string) => {
    setEmails((prev) => prev.delete(email));
  };

  const showSummary =
    fulfilledInvitations.length !== 0 ||
    rejectedInvitations.length !== 0 ||
    hardCapRejectedInvitations.length !== 0;

  const {isReady: hasFetchedLicenses} = doesUserBelongToAnyLicensedClassrooms(
    true // Query teacher's licenses
  );

  if (!hasFetchedLicenses || requestInFlight) {
    return (
      <Track>
        <Dialogue {...rest}>
          <Dialogue.Body>
            <LoadingSpinnerLong />
          </Dialogue.Body>
        </Dialogue>
      </Track>
    );
  }

  if (showSummary) {
    return (
      <Track>
        <IBESummary
          onBackBtnClick={() => {
            setFulfilledInvitations([]);
            setMustConfirmSubmission(false);
            setRejectedInvitations([]);
            setHardCapRejectedInvitations([]);
          }}
          fulfilledInvitations={fulfilledInvitations}
          hardCapRejectedInvitations={hardCapRejectedInvitations}
          handleClose={handleClose}
          rejectedInvitations={rejectedInvitations}
          {...rest}
        />
      </Track>
    );
  }

  if (mustConfirmSubmission) {
    const validEmails = emails.toList().filterNot(doesInvitationAlreadyExist);
    const invalidEmails = emails.toList().filter(doesInvitationAlreadyExist);

    return (
      <Track>
        <IBEConfirmEmails
          classroomId={classroomId}
          handleClose={handleClose}
          validEmails={validEmails}
          invalidEmails={invalidEmails}
          onBackBtnClick={() => setMustConfirmSubmission(false)}
          onConfirm={handleSend}
          {...rest}
        />
      </Track>
    );
  }

  const showBanner = restrictClassroomEnrollment && !emailDomains.isEmpty();

  return (
    <Track>
      <Dialogue
        handleClose={handleClose}
        {...rest}
        paddingLevel={0}
        size='m'
        className={classnames(rest.className, 'flex! max-h-full flex-col overflow-hidden!')}
      >
        <Dialogue.Title size='s' className='px-x2 pt-x1 shrink-0 grow-0!'>
          Invite Students by Email
        </Dialogue.Title>
        {hardCap && (
          <div className='shrink-0'>
            <HardCapWarningBanner seatsRemaining={seatsRemaining as number} />
          </div>
        )}
        <Dialogue.Body className='p-x3 min-h-x0 flex-1 overflow-y-auto'>
          <TrackedTextAreaField
            border
            value={value}
            className='mb-x2 max-w-[70ch]'
            error={Boolean(error)}
            label='Enter email address(es)'
            message={error}
            onChange={handleChange}
            onKeyPress={handleKeyPress}
            placeholder='Separate multiple email addressess using a comma, space, or semicolon'
            type='text'
            data-testid='invite-by-email-textarea'
          />
          <div className='flex items-center'>
            <TrackedButton className='mb-x2 ml-auto' onClick={() => handleSubmit()}>
              Add
            </TrackedButton>
          </div>
          <EmailChips
            className='ibe-email-list mt-x2 mb-x2 overflow-y-auto'
            emails={emails}
            handleClick={removeEmail}
          />
          <UploadCSVBtn className='pt-x2 mr-auto' onUpload={handleCSVUpload} />
          {showBanner && (
            <Banner className='mt-x2'>
              <span>
                Invitations are restricted to accounts ending in{' '}
                <ArrayToReadableFragment
                  arr={emailDomains.map((domain) => domain.getName()).toArray()}
                  as='strong'
                  conjunction='and'
                />
                .
              </span>
            </Banner>
          )}
        </Dialogue.Body>
        <div className='mt-x3 mb-x2 p-x2 flex shrink-0 items-center'>
          <TrackedButton className='ml-auto' color='secondary' onClick={handleClose}>
            Cancel
          </TrackedButton>
          <TrackedButton
            className='ml-x1'
            disabled={emails.isEmpty()}
            onClick={() => setMustConfirmSubmission(true)}
          >
            Send Invitations
          </TrackedButton>
        </div>
      </Dialogue>
    </Track>
  );
}
